import React from 'react';
import { motion } from 'framer-motion';

const team = [
  {
    id: 1,
    name: 'Minh',
    position: 'Founder',
    image: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400&q=80',
  },
  {
    id: 2,
    name: 'Anshu',
    position: 'Founder',
    image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&q=80',
  },
  {
    id: 3,
    name: 'Michelle',
    position: 'Designer Lead',
    image: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=400&q=80',
  },
  {
    id: 4,
    name: 'Aria',
    position: 'Designer Lead',
    image: 'https://images.unsplash.com/photo-1594744803329-e58b31de8bf5?w=400&q=80',
  },
  {
    id: 5,
    name: 'Prab',
    position: 'Construction Manager',
    image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&q=80',
  },
];

const timeline = [
  {
    year: '2021',
    text: 'Vesta Designs was founded with a bold vision to redefine modern architecture and interior design, blending innovation with purpose.',
  },
  {
    year: '2022',
    text: 'Broadened our reach into commercial and residential projects, delivering standout designs across multiple cities and regions.',
  },
  {
    year: '2023',
    text: 'Strengthened our team with talented designers and architects, quickly establishing Vesta Designs as pioneers in contemporary design.',
  },
  {
    year: '2024',
    text: 'Surpassed 1,000 completed projects, expanding our offices and forging partnerships with industry leaders worldwide.',
  },
  {
    year: '2025',
    text: 'Reached 2,000 projects and counting — continuing to push boundaries in sustainable design, modern architecture, and creating meaningful spaces.',
  },
];

const About = () => {
  return (
    <div className="about-page-v2">
      {/* Hero Section */}
      <section className="about-hero">
        <div 
          className="about-hero-bg"
          style={{ 
            backgroundImage: 'url(https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1920&q=80)' 
          }}
        />
        <div className="about-hero-overlay" />
        <div className="container">
          <motion.div 
            className="about-hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="about-hero-title">About Us</h1>
          </motion.div>
        </div>
      </section>

      {/* Vision Section */}
      <section className="aboutv2-vision">
        <div className="container">
          <div className="aboutv2-vision-content">
            <motion.h2 
              className="aboutv2-vision-title"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              Bringing Vision<br />to Reality.
            </motion.h2>
            <motion.p 
              className="aboutv2-vision-text"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Bringing vision to reality through innovative architecture and interior design, 
              we craft functional, aesthetic spaces that transform ideas into timeless environments 
              for modern living, working, and meaningful experiences.
            </motion.p>
          </div>
        </div>
        <motion.div 
          className="aboutv2-vision-image"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <img 
            src="https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=1400&q=80" 
            alt="Vesta Designs Vision"
          />
        </motion.div>
      </section>

      {/* Timeline Section */}
      <section className="aboutv2-timeline">
        <div className="aboutv2-timeline-container">
          <div className="aboutv2-timeline-track">
            <div className="aboutv2-timeline-line" />
            {timeline.map((item, index) => (
              <motion.div 
                key={index} 
                className="aboutv2-timeline-item"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <div className="aboutv2-timeline-year">{item.year}</div>
                <div className="aboutv2-timeline-dot">
                  <div className="aboutv2-timeline-dot-inner" />
                </div>
                <p className="aboutv2-timeline-text">{item.text}</p>
                <div className="aboutv2-timeline-icon">
                  <span>VD</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="aboutv2-stats">
        <div className="container">
          <motion.h2 
            className="aboutv2-stats-tagline"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            Innovate. Create. Elevate.
          </motion.h2>
          <div className="aboutv2-stats-grid">
            <motion.div 
              className="aboutv2-stat-item"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="aboutv2-stat-label">Projects Completed</span>
              <span className="aboutv2-stat-value">2000<span className="aboutv2-stat-plus">+</span></span>
            </motion.div>
            <motion.div 
              className="aboutv2-stat-item"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <span className="aboutv2-stat-label">Years of experience</span>
              <span className="aboutv2-stat-value">5</span>
            </motion.div>
            <motion.div 
              className="aboutv2-stat-item"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <span className="aboutv2-stat-label">Regional offices</span>
              <span className="aboutv2-stat-value">19<span className="aboutv2-stat-plus">+</span></span>
            </motion.div>
            <motion.div 
              className="aboutv2-stat-item"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <span className="aboutv2-stat-label">Team members</span>
              <span className="aboutv2-stat-value">20<span className="aboutv2-stat-plus">+</span></span>
            </motion.div>
          </div>
        </div>
      </section>

      {/* HIDDEN: Team section temporarily hidden - uncomment entire block below to restore */}
      {/*
      <section className="aboutv2-team">
        <div className="container">
          <motion.h2 
            className="aboutv2-team-title"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Meet Our Team
          </motion.h2>
          <motion.p 
            className="aboutv2-team-subtitle"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            Our talented team of architects and designers is dedicated to transforming 
            your vision into stunning, functional spaces.
          </motion.p>

          <div className="aboutv2-team-grid">
            {team.map((member, index) => (
              <motion.div 
                key={member.id}
                className="aboutv2-team-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <div className="aboutv2-team-image">
                  <img src={member.image} alt={member.name} />
                  <div className="aboutv2-team-overlay">
                    <div className="aboutv2-team-social">
                      <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
                        </svg>
                      </a>
                      <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6zM2 9h4v12H2zM4 6a2 2 0 1 0 0-4 2 2 0 0 0 0 4z"/>
                        </svg>
                      </a>
                      <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                          <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
                          <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
                        </svg>
                      </a>
                    </div>
                  </div>
                </div>
                <div className="aboutv2-team-info">
                  <h3 className="aboutv2-team-name">{member.name}</h3>
                  <p className="aboutv2-team-position">{member.position}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      */}
    </div>
  );
};

export default About;
